import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { LabelInput } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchAirtimePlans,
  updateAirtimePlan,
} from "../../../../features/auth/store/airtimePlanSlice";
import Modal from "../../../../components/Reusable/Modal";
import { fetchAirtimeProviders } from "../../../../features/auth/store/airtimeProviderSlice";
import { fetchAirtimeCategories } from "../../../../features/auth/store/airtimeCategorySlice";
import { fetchCountries } from "../../../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../../../features/auth/store/currencySlice";
import { EditAirTimePlan } from "../../Airtime/Plans/types";

type EditAirtimeProps = {
  isOpen: boolean;
  onClose?: () => void;
  airtimePlan?: EditAirTimePlan;
};

export const EditAirtimeModal: React.FC<EditAirtimeProps> = ({
  isOpen,
  onClose,
  airtimePlan,
}) => {
  // Providers and categories from redux
  const { airtimeProviders } = useAppSelector(
    (state) => state.airtimeProviders
  );
  const { airtimeCategories } = useAppSelector(
    (state) => state.airtimeCategories
  );

  const [formData, setFormData] = useState({
    provider_id: "",
    sub_category_id: "",
    name: "",
    message: "",
    status: "on",
    code: "",
    source: "",
    wallet_price: "",
    api_price: "",
    atm_price: "",
    discount: "",
    point_bonus: "",
    price_type: "fixed",
    price_currency_id: "",
    countries: [] as string[],
  });

  const [image, setImage] = useState<File | undefined>(undefined);
  const [currentImage, setcurrentImage] = useState<string | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);
  const countries = useAppSelector((state) => state.countries?.items ?? []);
  const countriesLoading = useAppSelector(
    (state) => state.countries?.loading ?? false
  );
  const currencies = useAppSelector((state) => state.currencies?.items ?? []);
  const deleteOldImage =
    airtimePlan?.image_url !== null && currentImage === undefined;

  // Fetch providers and countries on open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAirtimeProviders({}));
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (airtimePlan) {
      let countries = [] as string[];
      if (airtimePlan.countries) {
        const isString = airtimePlan.countries.every(
          (country) => typeof country === "string"
        );
        countries = isString
          ? (airtimePlan.countries as string[])
          : airtimePlan.countries.map((country) => String(country));
      }

      setcurrentImage(airtimePlan.image_url);
      setFormData({
        provider_id: airtimePlan.category.provider_id,
        sub_category_id: airtimePlan.category.id,
        wallet_price: airtimePlan.wallet_price?.toString() || "",
        api_price: airtimePlan?.api_price?.toString() || "",
        atm_price: airtimePlan?.atm_price?.toString() || "",
        discount: airtimePlan.discount?.toString() || "",
        point_bonus: airtimePlan.point_bonus?.toString() || "",
        status: airtimePlan.status,
        source: airtimePlan.source,
        code: airtimePlan.code || "",
        price_type: airtimePlan.price_type || "fixed",
        price_currency_id: airtimePlan.price_currency_id?.toString() || "",
        name: airtimePlan.name || "",
        message: airtimePlan.message,
        countries,
      });
    }
  }, [airtimePlan]);

  // Fetch categories when provider changes
  useEffect(() => {
    if (formData.provider_id) {
      dispatch(fetchAirtimeCategories({}));
    }
  }, [dispatch, formData.provider_id]);

  useEffect(() => {}, [airtimePlan]);

  // Filter categories for selected provider
  useEffect(() => {
    if (formData.provider_id && airtimeCategories.length) {
      setFilteredSubCategories(
        airtimeCategories.filter(
          (cat) => String(cat.provider_id) === String(formData.provider_id)
        )
      );
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.provider_id, airtimeCategories]);
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider_id" ? { sub_category_id: "" } : {}), // reset subcategory if provider changes
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]); // overwrite with the latest selected file
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const toggleCountry = (id: number) => {
    setFormData((prev) => {
      const exists = prev.countries.includes(String(id));
      return {
        ...prev,
        countries: exists
          ? prev.countries.filter((c) => c !== String(id))
          : [...prev.countries, String(id)],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!airtimePlan) {
      return;
    }

    const formDataObj = new FormData();

    // Append all form fields
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("sub_category_id", formData.sub_category_id);
    formDataObj.append("category_id", formData.sub_category_id); // <-- Ensure category_id is sent
    formDataObj.append("name", formData.name);
    formDataObj.append("message", formData.message || "");
    formDataObj.append("status", formData.status);
    formDataObj.append("code", formData.code || "");
    formDataObj.append(
      "countries",
      JSON.stringify(formData.countries.map((c) => Number(c)))
    );
    formDataObj.append("source", formData.source);
    formDataObj.append("wallet_price", formData.wallet_price);
    formDataObj.append("api_price", formData.api_price);
    formDataObj.append("atm_price", formData.atm_price);
    formDataObj.append("discount", formData.discount);
    formDataObj.append("point_bonus", formData.point_bonus);
    formDataObj.append("price_type", formData.price_type || "fixed");
    formDataObj.append("price_currency_id", formData.price_currency_id);
    if (image) {
      formDataObj.append("image", image);
    }
    if (deleteOldImage) {
      formDataObj.append("deleteOldImage", deleteOldImage.toString());
    }
    const filters: any = {};
    // Only add status filter if it's not empty
    if (formData.status && formData.status !== "") {
      filters.status = formData.status;
    }
    try {
      await dispatch(
        updateAirtimePlan({ id: airtimePlan?.id, data: formDataObj })
      ).unwrap();
      toast.success("Airtime plan Edited successfully!");
      handleClose();
      await dispatch(fetchAirtimePlans(filters)); // Refresh the list after creation
    } catch (error: any) {
      toast.error(error?.message || "Failed to Edit airtime plan");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    // Reset form
    setFormData({
      provider_id: "",
      sub_category_id: "",
      name: "",
      message: "",
      status: "on",
      code: "",
      countries: [],
      source: "",
      wallet_price: "",
      api_price: "",
      atm_price: "",
      discount: "",
      point_bonus: "",
      price_type: "fixed",
      price_currency_id: "",
    });
    setImage(undefined);
    setcurrentImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 w-[400px] max-h-[90vh] overflow-y-auto dark:bg-black dark:text-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-base font-semibold">Airtime Plan Details</p>
          {/* Provider */}
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...airtimeProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          {/* Sub Category */}
          <FloatingLabelSelect
            name="sub_category_id"
            placeholder="Sub Category"
            value={formData.sub_category_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Sub Category" },
              ...filteredSubCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            disabled={!formData.provider_id}
          />
          {/* Status */}
          <div className="space-y-2">
            <FloatingLabelSelect
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              placeholder="Status"
              options={[
                { value: "on", label: "Active" },
                { value: "off", label: "Inactive" },
              ]}
            />
          </div>

          {/* Product Name */}
          <div>
            <LabelInput
              type="text"
              name="name"
              placeholder="Product Name"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>

          {/* Wallet Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="wallet_price"
              placeholder="Wallet Price"
              value={formData.wallet_price}
              onChange={handleInputChange}
            />
          </div>

          {/* API Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="api_price"
              placeholder="API Price"
              value={formData.api_price}
              onChange={handleInputChange}
            />
          </div>

          {/* ATM Price */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="atm_price"
              placeholder="ATM Price"
              value={formData.atm_price}
              onChange={handleInputChange}
            />
          </div>

          {/* Discount Offered  */}
          <LabelInput
            type="number"
            name="discount"
            placeholder="Discount Offered"
            value={formData.discount}
            onChange={handleInputChange}
          />
          <LabelInput
            type="number"
            name="point_bonus"
            placeholder="Point Bonus"
            value={formData.point_bonus}
            onChange={handleInputChange}
          />
          {/* Price Type */}
          <FloatingLabelSelect
            name="price_type"
            placeholder="Price Type"
            value={formData.price_type}
            onChange={handleInputChange}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "percent", label: "Percent" },
            ]}
          />
          {/* Price Currency */}
          <FloatingLabelSelect
            name="price_currency_id"
            placeholder="Price Currency"
            value={formData.price_currency_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Currency" },
              ...currencies.map((c) => ({
                value: String(c.id),
                label: `${c.name} (${c.symbol})`,
              })),
            ]}
          />
          {/* Source */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="source"
              placeholder="Source"
              value={formData.source}
              onChange={handleInputChange}
            />
          </div>

          {/* Code */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="code"
              placeholder="Code (Optional)"
              value={formData.code}
              onChange={handleInputChange}
            />
          </div>
          {/* Description */}
          <div className="space-y-2">
            <p className="text-sm font-medium mb-1">Description</p>
            <textarea
              name="message"
              placeholder="Description"
              value={formData.message}
              onChange={handleInputChange}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500  dark:bg-white/10 dark:text-white"
            />
          </div>

          {/* Countries */}
          <h3 className="text-lg font-semibold text-gray-800 mb-2 dark:text-white">
            Countries (availability)
          </h3>
          <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
            {countriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : countries.length === 0 ? (
              <p className="text-sm text-gray-500">
                No countries found. Create countries first.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {countries.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(String(c.id))}
                      onChange={() => toggleCountry(c.id)}
                    />
                    <span>
                      {c.name} ({c.symbol})
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Product Images */}
          <div className="space-y-4">
            <p className="text-base font-semibold">Product Image</p>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center  dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
              />
              <label
                htmlFor="fileUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                {/* Upload icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Upload from computer
                </span>
              </label>
            </div>

            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="product"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage()}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {currentImage && (
              <div>
                <p className="text-base font-semibold">Current Product Image</p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20">
                    <img
                      src={currentImage}
                      alt="product"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setcurrentImage(undefined)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            <Button
              type="submit"
              disabled={isSubmitting}
              bgColor={
                isSubmitting
                  ? "bg-gray-300"
                  : "bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              }
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-full h-10"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
